import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService, IDLE_TIMEOUT_MS, COUNTDOWN_SECONDS, LAST_ACTIVITY_KEY } from '../services/auth';
import { refreshSession } from '../services/api';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const COUNTDOWN_MS = COUNTDOWN_SECONDS * 1000;
// Renew the token in the background once it has less than this left (tokens last 30 min)
const RENEW_BEFORE_EXPIRY_MS = 10 * 60 * 1000;
const RENEW_RETRY_MS = 60 * 1000;
// Signed-in pages (see ProtectedRoute usage in App.js); public pages just drop the session quietly
const PROTECTED_PATH = /^\/(admin|dashboard|notifications|subscription)(\/|$)/;

// Idle state is timed from the lastActivity timestamp in localStorage, so it is
// shared across tabs and still applies after sleep or a browser restart.
const IdleTimeout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [extending, setExtending] = useState(false);
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);
  const deadlineRef = useRef(0);
  const warningRef = useRef(false);
  const lastRenewAttemptRef = useRef(0);

  const clearIdleTimer = () => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
  };

  const hideWarning = () => {
    clearCountdown();
    warningRef.current = false;
    setWarning(false);
  };

  const doLogout = useCallback((message) => {
    clearIdleTimer();
    clearCountdown();
    warningRef.current = false;
    setWarning(false);
    setExtending(false);
    authService.logout();
    if (PROTECTED_PATH.test(location.pathname)) {
      navigate('/login', { replace: true });
      toast.warn(message);
    }
  }, [navigate, location.pathname]);

  const showWarning = useCallback((deadline) => {
    warningRef.current = true;
    deadlineRef.current = deadline;
    setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    setWarning(true);
  }, []);

  // Warn, sign out, or re-arm based on time since the last recorded activity
  const checkIdle = useCallback(() => {
    clearIdleTimer();
    if (!authService.getToken() || warningRef.current) return;
    const lastActivity = authService.getLastActivity();
    const elapsed = Date.now() - lastActivity;
    if (elapsed >= IDLE_TIMEOUT_MS + COUNTDOWN_MS) {
      doLogout('Signed out due to inactivity. Please log in again.');
    } else if (elapsed >= IDLE_TIMEOUT_MS) {
      showWarning(lastActivity + IDLE_TIMEOUT_MS + COUNTDOWN_MS);
    } else {
      idleTimer.current = setTimeout(checkIdle, IDLE_TIMEOUT_MS - elapsed);
    }
  }, [doLogout, showWarning]);

  // Keep an active user's token fresh; a 401 from refresh is handled by the API interceptor
  const maybeRenewToken = () => {
    const expiresAt = authService.tokenExpiresAt();
    const now = Date.now();
    if (!expiresAt || expiresAt - now > RENEW_BEFORE_EXPIRY_MS) return;
    if (now - lastRenewAttemptRef.current < RENEW_RETRY_MS) return;
    lastRenewAttemptRef.current = now;
    refreshSession().catch(() => {});
  };

  // Countdown runner — counts to a fixed deadline so sleep can't pause it.
  // Only explicit button clicks (or activity in another tab) stop it.
  useEffect(() => {
    if (!warning) return;
    countdownTimer.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearCountdown();
        doLogout('Signed out due to inactivity. Please log in again.');
      }
    }, 1000);
    return () => clearCountdown();
  }, [warning, doLogout]);

  // Arm on mount and route change (e.g. right after login); track activity.
  useEffect(() => {
    checkIdle();
    const handleActivity = () => {
      if (warningRef.current || !authService.getToken()) return;
      authService.touchActivity();
      checkIdle();
      maybeRenewToken();
    };
    // Timers don't run while the laptop sleeps or the tab is frozen — recheck on return
    const handleVisible = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };
    // Other tabs: their activity keeps this tab alive; their sign-out signs this tab out
    const handleStorage = (event) => {
      if (event.key === 'authToken' && !event.newValue) {
        doLogout('You have been signed out.');
      } else if (event.key === LAST_ACTIVITY_KEY || event.key === 'authToken') {
        if (warningRef.current) hideWarning();
        checkIdle();
      }
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', checkIdle);
    window.addEventListener('storage', handleStorage);
    return () => {
      clearIdleTimer();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', checkIdle);
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIdle, doLogout]);

  const handleExtend = async () => {
    if (extending) return;
    setExtending(true);
    try {
      await refreshSession();
      authService.touchActivity();
      hideWarning();
      setExtending(false);
      checkIdle();
      toast.success('Session extended. You are still signed in.');
    } catch (error) {
      doLogout('Your session has expired. Please log in again.');
    }
  };

  const handleSignOut = () => {
    doLogout('You have been signed out.');
  };

  if (!warning || !authService.getToken()) return null;

  const urgent = secondsLeft <= 10;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
        <h3 className="text-lg font-semibold mb-2">Are you still there?</h3>
        <p className="text-sm text-gray-600 mb-4">
          You have been idle for 15 minutes. For your security you will be signed out soon.
        </p>
        <div className={`text-5xl font-bold my-4 ${urgent ? 'text-red-600' : 'text-gray-800'}`}>
          {secondsLeft}
        </div>
        <p className="text-xs text-gray-500 mb-4">seconds until automatic sign-out</p>
        <div className="flex justify-center space-x-3">
          <button
            onClick={handleExtend}
            disabled={extending}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {extending ? 'Extending...' : 'Extend session'}
          </button>
          <button
            onClick={handleSignOut}
            disabled={extending}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleTimeout;
